import { type HTTPHeaders } from './generated/runtime';
import { type CreateApiOptions } from './create-api';

/**
 * Configuration options for initializing the API client.
 */
export interface ApiClientOptions {
  /**
   * Function that returns the User-Agent string for API requests.
   * This is platform-specific (e.g., React Native Platform info).
   */
  getUserAgent?: () => string;

  /**
   * Callback invoked when a 401 Unauthorized response is received.
   * Useful for triggering re-authentication flows.
   */
  onUnauthorized?: () => void;

  /**
   * Extra headers attached to every request. Used by Path A mini-apps
   * to declare their identity to the backend's scope guard (e.g.
   * `X-Mini-App-Id: com.linqx.mylinqx-card`).
   */
  extraHeaders?: HTTPHeaders;
}

// Global configuration store
let globalOptions: ApiClientOptions = {};
let isInitialized = false;

// Registry of cache clear callbacks from individual API modules
const cacheCleaners: Array<() => void> = [];

/**
 * Register a cache cleaner callback. Called by individual API modules.
 * @internal
 */
export const registerCacheCleaner = (cleaner: () => void): void => {
  cacheCleaners.push(cleaner);
};

/**
 * Initialize the API client with platform-specific options.
 * Call this early in your application lifecycle (e.g., in _layout.tsx for Expo).
 *
 * @param options - Configuration options including getUserAgent and onUnauthorized callbacks
 *
 * @example
 * ```typescript
 * import { initApiClient } from '@linqx/api-client/client';
 * import { getUserAgent } from './utils/getUserAgent';
 *
 * initApiClient({
 *   getUserAgent,
 *   onUnauthorized: () => {
 *     // Handle logout or re-auth
 *   },
 * });
 * ```
 */
export const initApiClient = (options: ApiClientOptions = {}): void => {
  globalOptions = options;
  isInitialized = true;
  // Clear all cached API instances
  cacheCleaners.forEach((cleaner) => cleaner());
};

/**
 * Get the current API client options.
 */
export const getApiClientOptions = (): CreateApiOptions => globalOptions;

/**
 * Check if the API client has been initialized.
 */
export const isApiClientInitialized = (): boolean => isInitialized;
